import React from 'react'
import { Menu, MenuProps } from 'antd'
import {
  AppstoreAddOutlined,
  BarsOutlined,
  CustomerServiceOutlined,
  GlobalOutlined,
  HddOutlined,
  PercentageOutlined,
  RiseOutlined,
  UserOutlined,
  SwitcherOutlined,
  UsergroupAddOutlined
} from '@ant-design/icons'
import { useLocation, useNavigate } from 'react-router-dom'
import { superAdminRoutes } from 'shared/constants/routes'
import { getWindowWidth } from 'shared/utils/getWindowWidth'
import { useActionsCounters } from 'shared/hooks/useActionsCounters'
import { useIsTouchLandscape } from 'shared/hooks/useIsTouchLandscape'
import { useAppSelector } from 'shared/hooks/useReduxHooks'
import { horsesSelectors } from 'store/horses'
import { reviewsSelectors } from 'store/reviews'
import { withdrawalRequestsSelectors } from 'store/withdrawalRequests'
import { supportSelectors } from 'store/support'
import { HorseIcon } from 'components/ui'
import { MenuCountBadge } from '../MenuCountBadge/MenuCountBadge'
import { MobileMenu } from '../MobileMenu/MobileMenu'

const usersMenuItems = [
  {
    label: 'Продавцы',
    key: superAdminRoutes.SELLERS,
    icon: <UsergroupAddOutlined />
  },
  {
    label: 'Пользователи',
    key: superAdminRoutes.USERS,
    icon: <UserOutlined />
  },
  {
    label: 'Поддержка',
    key: superAdminRoutes.SUPPORT,
    icon: <CustomerServiceOutlined />
  }
]

const collectionsMenuItems = [
  { label: 'Продвижение', key: superAdminRoutes.MAIN, icon: <RiseOutlined /> },
  {
    label: 'Промокоды',
    key: superAdminRoutes.PROMOCODES,
    icon: <SwitcherOutlined />
  },
  {
    label: 'Скидки',
    key: superAdminRoutes.DISCOUNTS,
    icon: <PercentageOutlined />
  },
  { label: 'ПолоКлуб', key: superAdminRoutes.POLO_CLUB, icon: <HorseIcon /> }
]

export const SuperAdminMenu: React.FC = () => {
  const location = useLocation()
  const navigate = useNavigate()

  useActionsCounters()

  const pendingReviewsCount = useAppSelector(reviewsSelectors.selectPendingCount)
  const pendingWithdrawalRequestsCount = useAppSelector(
    withdrawalRequestsSelectors.selectPendingCount
  )
  const actionsCount = pendingReviewsCount + pendingWithdrawalRequestsCount
  const supportUnreadCount = useAppSelector(supportSelectors.selectTotalUnread)
  const moderationHorsesCount = useAppSelector(horsesSelectors.selectModerationCount)

  const isTouchLandscape = useIsTouchLandscape()
  const [windowWidth, setWindowWidth] = React.useState<number>(getWindowWidth)
  const [isOpenMobileMenu, setIsOpenMobileMenu] = React.useState(false)
  const [current, setCurrent] = React.useState('')

  const [currentUserMenu, setCurrentUserMenu] = React.useState(
    superAdminRoutes.SELLERS
  )

  const [currentCollectionsMenu, setCurrentCollectionsMenu] = React.useState(
    superAdminRoutes.MAIN
  )

  const [currentGoodsMenu, setCurrentGoodsMenu] = React.useState(
    superAdminRoutes.GOODS
  )

  const currentUsersItem =
    usersMenuItems.find((i) => i.key === currentUserMenu) ?? usersMenuItems[0]

  const currentCollectionsItem =
    collectionsMenuItems.find((i) => i.key === currentCollectionsMenu) ??
    collectionsMenuItems[0]

  const isCurrentHorses = current === superAdminRoutes.HORSES

  const onClick: MenuProps['onClick'] = (e) => {
    setIsOpenMobileMenu(false)
    if (
      e.key === superAdminRoutes.GOODS ||
      e.key === superAdminRoutes.CATEGORIES ||
      e.key === superAdminRoutes.HORSES
    ) {
      setCurrentGoodsMenu(e.key)
    }
    navigate(e.key)
  }

  const items: MenuProps['items'] = React.useMemo(
    () => [
      {
        label: (
          <MenuCountBadge count={supportUnreadCount}>
            {`${currentUsersItem.label} ↓`}
          </MenuCountBadge>
        ),
        key: currentUserMenu,
        icon: currentUsersItem.icon,
        onTitleClick: (e: any) => onClick(e),
        children: usersMenuItems
          .filter((item) => item.key !== currentUserMenu)
          .map(({ label, key }) => ({
            key,
            label: key === superAdminRoutes.SUPPORT ? (
              <MenuCountBadge count={supportUnreadCount}>{label}</MenuCountBadge>
            ) : label
          }))
      },
      {
        label: 'Заказы',
        key: superAdminRoutes.ORDERS,
        icon: <HddOutlined />
      },
      {
        label: `${currentCollectionsItem.label} ↓`,
        key: currentCollectionsMenu,
        icon: currentCollectionsItem.icon,
        onTitleClick: (e: any) => onClick(e),
        children: collectionsMenuItems
          .filter((i) => i.key !== currentCollectionsMenu)
          .map(({ label, key }) => ({ label, key }))
      },
      {
        label: (
          <MenuCountBadge count={moderationHorsesCount}>
            {`${
              currentGoodsMenu === superAdminRoutes.GOODS
                ? 'Товары'
                : currentGoodsMenu === superAdminRoutes.CATEGORIES
                  ? 'Категории'
                  : 'Лошади'
              // eslint-disable-next-line indent
            } ↓`}
          </MenuCountBadge>
        ),
        key: currentGoodsMenu,
        icon:
          currentGoodsMenu === superAdminRoutes.HORSES ? (
            <svg
              width='16'
              height='16'
              viewBox='0 0 16 16'
              fill='none'
              xmlns='http://www.w3.org/2000/svg'
            >
              <g id='Layer_1' clipPath='url(#clip0_6549_12417)'>
                <path
                  id='Vector'
                  d='M0.00150473 13.0234H0.00125394C0.00125394 13.9265 0.00175551 14.8296 0 15.7328C0 15.786 0.0135425 15.7981 0.0652048 15.7976C0.380696 15.7951 0.696186 15.7953 1.01168 15.7976C1.05832 15.7979 1.07136 15.7862 1.07111 15.7383C1.06886 15.424 1.07187 15.1095 1.06836 14.7952C1.0676 14.7324 1.08089 14.7131 1.1466 14.7134C1.63137 14.7164 2.11615 14.7134 2.60092 14.7167C2.66688 14.7172 2.67942 14.6969 2.67891 14.6349C2.6759 14.3246 2.67615 14.0147 2.67891 13.7044C2.67942 13.6467 2.66437 13.6307 2.60644 13.631C2.1174 13.6335 1.62861 13.6312 1.13958 13.634C1.08014 13.6343 1.06886 13.616 1.06911 13.5601C1.07111 13.0685 1.07136 12.5769 1.06861 12.0851C1.06836 12.0238 1.08792 12.0131 1.14259 12.0131C1.63363 12.0152 2.12467 12.0137 2.61572 12.0154C2.66512 12.0154 2.67942 12.0012 2.67891 11.9514C2.67691 11.6371 2.67616 11.3225 2.67916 11.0082C2.67967 10.9467 2.66236 10.9312 2.60242 10.9315C2.11339 10.9338 1.6246 10.9327 1.13557 10.933C1.0982 10.933 1.06836 10.9368 1.06936 10.8798C1.07839 10.3728 1.11575 9.86905 1.21908 9.37165C1.22886 9.32505 1.24516 9.31163 1.29206 9.31188C1.73094 9.31391 2.16956 9.31188 2.60844 9.31416C2.66186 9.31441 2.67967 9.30352 2.67891 9.24528C2.67565 8.93098 2.6759 8.61643 2.67891 8.30214C2.67942 8.24642 2.66562 8.23173 2.6102 8.23198C2.24254 8.23452 1.87464 8.23249 1.50698 8.23452C1.45407 8.23477 1.4483 8.21856 1.46184 8.17247C1.61131 7.66418 1.80216 7.17387 2.06699 6.7142C2.57609 5.82982 3.30287 5.19237 4.22101 4.77145C4.3306 4.72105 4.3316 4.72308 4.34339 4.84667C4.34339 4.84869 4.34389 4.85097 4.34414 4.853C4.40835 5.52642 4.62177 6.15982 4.85425 6.78815C4.86503 6.81753 4.87581 6.8264 4.90892 6.81399C5.21939 6.69698 5.53012 6.58149 5.8416 6.46727C5.87896 6.4536 5.88423 6.43891 5.86918 6.40269C5.83031 6.30975 5.79671 6.21452 5.75934 6.12081C5.48021 5.42233 5.33526 4.69851 5.35081 3.9438C5.35256 3.85921 5.35632 3.77513 5.37112 3.6918C5.44109 3.29545 5.61313 2.94494 5.85113 2.6266C6.12198 2.26393 6.45904 1.98585 6.88613 1.82832C6.9566 1.80224 6.9571 1.80325 6.9571 1.87745C6.9571 2.66028 6.9576 3.4431 6.95585 4.22593C6.95585 4.27455 6.97039 4.29557 7.01528 4.31052C7.2904 4.40144 7.56501 4.49514 7.83937 4.58809C8.63161 4.8568 9.4236 5.12576 10.2158 5.39421C10.917 5.63202 11.6185 5.86908 12.3197 6.10663C13.0051 6.33887 13.688 6.57947 14.3767 6.80132C14.7629 6.92567 14.9873 7.26327 14.9828 7.6462C14.976 8.2411 14.9808 8.83626 14.9813 9.43117C14.9813 9.50309 14.978 9.57502 14.9637 9.64517C14.8612 10.1514 14.3937 10.4792 13.8891 10.3981C13.3121 10.3054 12.7355 10.211 12.1587 10.1175C11.8537 10.0681 11.5488 10.0187 11.2436 9.97112C11.1969 9.96377 11.1824 9.94984 11.1937 9.89843C11.234 9.71836 11.234 9.5345 11.2365 9.35088C11.237 9.31391 11.2197 9.31087 11.1909 9.31112C10.8649 9.31163 10.5391 9.31214 10.2131 9.31062C10.174 9.31062 10.1629 9.32353 10.1622 9.36101C10.1599 9.4727 10.1511 9.58388 10.1303 9.69405C9.94549 10.679 9.14974 11.4 8.16138 11.4648C7.50908 11.5076 6.95058 11.283 6.49415 10.8135C6.10843 10.4166 5.9078 9.93414 5.88849 9.37697C5.88724 9.34025 5.891 9.3096 5.83457 9.31011C5.51081 9.31289 5.18679 9.31214 4.86302 9.31062C4.8244 9.31062 4.81312 9.32125 4.81437 9.36051C4.82139 9.59503 4.84622 9.82701 4.90039 10.0555C5.05312 10.7005 5.36786 11.2511 5.84987 11.7019C6.29602 12.1193 6.81967 12.3827 7.4193 12.4867C7.47774 12.4969 7.49303 12.5164 7.4973 12.5721C7.53316 13.0333 7.61316 13.4869 7.73454 13.9331C7.9294 14.6503 8.23511 15.32 8.58923 15.9691C8.61155 16.0101 8.63161 16.0027 8.66346 15.9865C8.95212 15.8396 9.24052 15.6928 9.53068 15.5489C9.57307 15.5279 9.57156 15.5114 9.55225 15.4744C9.39601 15.1769 9.24554 14.8762 9.11111 14.5678C8.83048 13.9237 8.63412 13.2564 8.56615 12.5533C8.56264 12.5174 8.56339 12.4976 8.60879 12.4895C8.98672 12.4217 9.33883 12.2844 9.66385 12.0777C10.1012 11.7994 10.4466 11.4296 10.7189 10.9885C10.7407 10.9533 10.759 10.932 10.8067 10.9406C11.0367 10.9821 11.2674 11.0196 11.4979 11.0586C11.854 11.1186 12.2101 11.1774 12.566 11.2392C12.9976 11.3141 13.4317 11.3757 13.8618 11.461C13.9004 11.4686 13.9405 11.475 13.9799 11.474C14.5018 11.4626 14.9612 11.2865 15.3439 10.9264C15.7843 10.5116 15.9962 9.9934 15.9977 9.38685C15.9992 8.73268 15.9998 8.07876 16.0008 7.42459C16.0008 7.31468 15.9905 7.20527 15.9636 7.09865C15.8114 6.49412 15.4292 6.08865 14.8735 5.84628C14.5695 5.71383 14.2477 5.62721 13.9335 5.5211C12.9926 5.20351 12.0513 4.88719 11.1104 4.57036C10.106 4.23201 9.10209 3.89315 8.09718 3.55631C8.04326 3.53833 8.02445 3.51706 8.02445 3.45703C8.02696 2.54986 8.02546 1.64243 8.02771 0.735002C8.02771 0.677765 8.01292 0.665102 7.95875 0.666622C7.81254 0.670674 7.66633 0.668395 7.51987 0.667888C7.24902 0.667381 6.98268 0.700558 6.72161 0.77375C6.15784 0.932037 5.68285 1.23797 5.28384 1.66548C4.80033 2.18339 4.47831 2.78818 4.34289 3.48996C4.33612 3.52491 4.31982 3.53985 4.28997 3.55099C4.13749 3.60798 3.98828 3.67332 3.84106 3.74271C3.19428 4.04662 2.60719 4.43917 2.09709 4.94519C1.38936 5.64773 0.906848 6.49057 0.580573 7.43042C0.347842 8.10105 0.203389 8.7927 0.116365 9.49651C0.0416307 10.0937 0.00376181 10.6934 0.0020063 11.2954C0.000250788 11.8713 0.00150473 12.4475 0.00150473 13.0234Z'
                  fill={isCurrentHorses ? '#1677ff' : 'black'}
                  fillOpacity='0.88'
                />
                <path
                  id='Vector_2'
                  d='M0.0020063 13.0234C0.0020063 12.4475 0.000501575 11.8713 0.00250788 11.2954C0.00426339 10.6937 0.0421323 10.0937 0.115864 9.49675C0.202887 8.79294 0.347341 8.10129 0.580072 7.43066C0.906347 6.49082 1.38886 5.64797 2.09658 4.94543C2.60644 4.43916 3.19378 4.04686 3.84056 3.74295C3.98802 3.67381 4.13699 3.60847 4.28947 3.55124C4.31932 3.54009 4.33562 3.52515 4.34239 3.4902C4.47781 2.78867 4.79982 2.18364 5.28334 1.66572C5.68235 1.23822 6.15734 0.932028 6.72111 0.773995C6.98268 0.700803 7.24902 0.667626 7.51987 0.668132C7.66608 0.668385 7.81254 0.670918 7.95875 0.666866C8.01317 0.665346 8.02796 0.678009 8.02771 0.735246C8.02571 1.64242 8.02696 2.54985 8.02445 3.45728C8.02445 3.5173 8.04326 3.53857 8.09718 3.55655C9.10209 3.89314 10.1062 4.23225 11.1104 4.5706C12.0513 4.88769 12.9926 5.20401 13.9335 5.52134C14.2477 5.62746 14.5695 5.71382 14.8735 5.84653C15.4292 6.08889 15.8114 6.49436 15.9636 7.09889C15.9905 7.20526 16.0008 7.31467 16.0008 7.42484C16 8.07901 15.9995 8.73292 15.9977 9.38709C15.9962 9.99365 15.7846 10.5118 15.3439 10.9267C14.9612 11.287 14.5018 11.4631 13.9799 11.4742C13.9405 11.475 13.9007 11.4689 13.8618 11.4613C13.4317 11.3759 12.9978 11.3146 12.566 11.2394C12.2103 11.1776 11.854 11.1191 11.4979 11.0589C11.2674 11.0199 11.0367 10.9824 10.8067 10.9408C10.759 10.9322 10.7405 10.9535 10.7189 10.9887C10.4466 11.4299 10.1015 11.7996 9.66385 12.078C9.33883 12.2846 8.98672 12.4219 8.60879 12.4898C8.56339 12.4979 8.56264 12.5176 8.56615 12.5536C8.63412 13.2564 8.83023 13.924 9.11111 14.568C9.24554 14.8765 9.39601 15.1771 9.55225 15.4747C9.57181 15.5119 9.57332 15.5281 9.53068 15.5491C9.24077 15.693 8.95212 15.8401 8.66346 15.9868C8.63161 16.003 8.61155 16.0101 8.58923 15.9693C8.23511 15.3202 7.9294 14.6506 7.73454 13.9333C7.61316 13.4871 7.53341 13.0335 7.4973 12.5723C7.49303 12.5169 7.47774 12.4971 7.4193 12.487C6.81967 12.3829 6.29602 12.1195 5.84987 11.7021C5.36786 11.2513 5.05312 10.7007 4.90039 10.0557C4.84622 9.82726 4.82139 9.59527 4.81437 9.36075C4.81312 9.3215 4.8244 9.31086 4.86302 9.31086C5.18679 9.31238 5.51081 9.31314 5.83457 9.31035C5.89075 9.30985 5.88724 9.34049 5.88849 9.37721C5.90755 9.93464 6.10818 10.4168 6.49415 10.8137C6.95033 11.2832 7.50883 11.5079 8.16138 11.4651C9.14949 11.4003 9.94549 10.6792 10.1303 9.69429C10.1511 9.58413 10.1599 9.47295 10.1622 9.36126C10.1629 9.32378 10.174 9.31086 10.2131 9.31086C10.5391 9.31238 10.8649 9.31187 11.1909 9.31137C11.2197 9.31137 11.237 9.31415 11.2365 9.35113C11.234 9.53474 11.234 9.71861 11.1937 9.89867C11.1821 9.95009 11.1967 9.96402 11.2436 9.97136C11.5488 10.019 11.8537 10.0684 12.1587 10.1177C12.7355 10.2112 13.3121 10.3059 13.8891 10.3984C14.3937 10.4794 14.8612 10.1517 14.9637 9.64542C14.978 9.57526 14.9813 9.50334 14.9813 9.43141C14.9808 8.8365 14.976 8.24135 14.9828 7.64644C14.9871 7.26351 14.7629 6.92592 14.3767 6.80157C13.688 6.57971 13.0051 6.33937 12.3197 6.10688C11.6185 5.86907 10.917 5.63201 10.2158 5.39446C9.4236 5.126 8.63161 4.85704 7.83937 4.58833C7.56476 4.49513 7.2904 4.40168 7.01528 4.31076C6.97039 4.29582 6.95585 4.2748 6.95585 4.22617C6.9576 3.44335 6.9571 2.66052 6.9571 1.8777C6.9571 1.80349 6.95685 1.80248 6.88613 1.82857C6.45904 1.98609 6.12198 2.26417 5.85113 2.62684C5.61338 2.94519 5.44109 3.2957 5.37112 3.69205C5.35632 3.77537 5.35256 3.85945 5.35081 3.94404C5.33501 4.69875 5.48021 5.42282 5.75934 6.12106C5.79671 6.21476 5.83031 6.30999 5.86918 6.40294C5.88423 6.43915 5.87896 6.45359 5.8416 6.46752C5.53012 6.58174 5.21939 6.69722 4.90892 6.81423C4.87606 6.82664 4.86528 6.81778 4.85425 6.7884C4.62151 6.16006 4.40835 5.52691 4.34414 4.85324C4.34414 4.85122 4.34364 4.84894 4.34339 4.84691C4.3316 4.72332 4.3306 4.72129 4.22101 4.77169C3.30262 5.19261 2.57609 5.83006 2.06699 6.71445C1.80241 7.17411 1.61131 7.66442 1.46184 8.17271C1.4483 8.21855 1.45432 8.23501 1.50698 8.23476C1.87464 8.23274 2.24254 8.23476 2.6102 8.23223C2.66562 8.23172 2.67942 8.24641 2.67891 8.30238C2.6759 8.61668 2.6759 8.93122 2.67891 9.24552C2.67942 9.30352 2.66186 9.31466 2.60844 9.31441C2.16956 9.31238 1.73094 9.3139 1.29206 9.31213C1.24516 9.31213 1.22886 9.32555 1.21908 9.3719C1.1155 9.8693 1.07814 10.373 1.06936 10.8801C1.06836 10.937 1.0982 10.9332 1.13557 10.9332C1.6246 10.933 2.11339 10.934 2.60242 10.9317C2.66236 10.9315 2.67992 10.9469 2.67916 11.0085C2.67615 11.3228 2.67666 11.6373 2.67891 11.9516C2.67916 12.0012 2.66512 12.0159 2.61572 12.0157C2.12467 12.0139 1.63363 12.0154 1.14259 12.0134C1.08792 12.0134 1.0681 12.024 1.06861 12.0853C1.07136 12.5769 1.07111 13.0685 1.06911 13.5603C1.06911 13.6163 1.08039 13.6345 1.13958 13.6343C1.62861 13.6317 2.1174 13.6337 2.60644 13.6312C2.66437 13.631 2.67942 13.6469 2.67891 13.7047C2.67615 14.0149 2.6759 14.3249 2.67891 14.6351C2.67942 14.6972 2.66688 14.7174 2.60092 14.7169C2.11615 14.7139 1.63137 14.7169 1.1466 14.7136C1.08089 14.7131 1.06785 14.7326 1.06836 14.7954C1.07162 15.1097 1.06886 15.4243 1.07111 15.7386C1.07136 15.7864 1.05832 15.7981 1.01168 15.7978C0.696186 15.7958 0.380696 15.7953 0.0652048 15.7978C0.0135425 15.7984 0 15.7862 0 15.733C0.00175551 14.8299 0.00125394 13.9268 0.00125394 13.0236H0.00150473L0.0020063 13.0234Z'
                  fill={isCurrentHorses ? '#1677ff' : 'black'}
                  fillOpacity='0.88'
                />
              </g>
              <defs>
                <clipPath id='clip0_6549_12417'>
                  <rect
                    width='16'
                    height='15.3333'
                    fill='white'
                    transform='translate(0 0.666626)'
                  />
                </clipPath>
              </defs>
            </svg>
          ) : currentGoodsMenu === superAdminRoutes.GOODS ? (
            <AppstoreAddOutlined />
          ) : (
            <BarsOutlined />
          ),
        onTitleClick: (e: any) => onClick(e),
        children: [
          {
            label: 'Товары',
            key: superAdminRoutes.GOODS
          },
          {
            label: 'Категории',
            key: superAdminRoutes.CATEGORIES
          },
          {
            label: (
              <MenuCountBadge count={moderationHorsesCount}>Лошади</MenuCountBadge>
            ),
            key: superAdminRoutes.HORSES
          }
        ].filter((item) => item.key !== currentGoodsMenu)
      },
      {
        label: <MenuCountBadge count={actionsCount}>Действия</MenuCountBadge>,
        key: superAdminRoutes.ACTIONS,
        icon: <GlobalOutlined />
      }
    ],
    [
      currentUserMenu,
      currentUsersItem,
      currentCollectionsItem,
      currentCollectionsMenu,
      currentGoodsMenu,
      isCurrentHorses,
      actionsCount,
      supportUnreadCount,
      moderationHorsesCount
    ]
  )

  const mobileItems: MenuProps['items'] = React.useMemo(
    () => [
      {
        label: (
          <MenuCountBadge count={supportUnreadCount}>Поддержка</MenuCountBadge>
        ),
        key: superAdminRoutes.SUPPORT,
        icon: <CustomerServiceOutlined />
      },
      {
        label: 'Продавцы',
        key: superAdminRoutes.SELLERS,
        icon: <UsergroupAddOutlined />
      },
      {
        label: 'Пользователи',
        key: superAdminRoutes.USERS,
        icon: <UserOutlined />
      },
      {
        label: 'Заказы',
        key: superAdminRoutes.ORDERS,
        icon: <HddOutlined />
      },
      {
        label: 'Товары',
        key: superAdminRoutes.GOODS,
        icon: <AppstoreAddOutlined />
      },
      {
        label: 'Категории',
        key: superAdminRoutes.CATEGORIES,
        icon: <BarsOutlined />
      },
      {
        label: (
          <MenuCountBadge count={moderationHorsesCount}>Лошади</MenuCountBadge>
        ),
        key: superAdminRoutes.HORSES,
        icon: (
          <svg
            width='16'
            height='16'
            viewBox='0 0 16 16'
            fill='none'
            xmlns='http://www.w3.org/2000/svg'
          >
            <g id='Layer_1' clipPath='url(#clip0_6549_12417)'>
              <path
                id='Vector'
                d='M0.00150473 13.0234H0.00125394C0.00125394 13.9265 0.00175551 14.8296 0 15.7328C0 15.786 0.0135425 15.7981 0.0652048 15.7976C0.380696 15.7951 0.696186 15.7953 1.01168 15.7976C1.05832 15.7979 1.07136 15.7862 1.07111 15.7383C1.06886 15.424 1.07187 15.1095 1.06836 14.7952C1.0676 14.7324 1.08089 14.7131 1.1466 14.7134C1.63137 14.7164 2.11615 14.7134 2.60092 14.7167C2.66688 14.7172 2.67942 14.6969 2.67891 14.6349C2.6759 14.3246 2.67615 14.0147 2.67891 13.7044C2.67942 13.6467 2.66437 13.6307 2.60644 13.631C2.1174 13.6335 1.62861 13.6312 1.13958 13.634C1.08014 13.6343 1.06886 13.616 1.06911 13.5601C1.07111 13.0685 1.07136 12.5769 1.06861 12.0851C1.06836 12.0238 1.08792 12.0131 1.14259 12.0131C1.63363 12.0152 2.12467 12.0137 2.61572 12.0154C2.66512 12.0154 2.67942 12.0012 2.67891 11.9514C2.67691 11.6371 2.67616 11.3225 2.67916 11.0082C2.67967 10.9467 2.66236 10.9312 2.60242 10.9315C2.11339 10.9338 1.6246 10.9327 1.13557 10.933C1.0982 10.933 1.06836 10.9368 1.06936 10.8798C1.07839 10.3728 1.11575 9.86905 1.21908 9.37165C1.22886 9.32505 1.24516 9.31163 1.29206 9.31188C1.73094 9.31391 2.16956 9.31188 2.60844 9.31416C2.66186 9.31441 2.67967 9.30352 2.67891 9.24528C2.67565 8.93098 2.6759 8.61643 2.67891 8.30214C2.67942 8.24642 2.66562 8.23173 2.6102 8.23198C2.24254 8.23452 1.87464 8.23249 1.50698 8.23452C1.45407 8.23477 1.4483 8.21856 1.46184 8.17247C1.61131 7.66418 1.80216 7.17387 2.06699 6.7142C2.57609 5.82982 3.30287 5.19237 4.22101 4.77145C4.3306 4.72105 4.3316 4.72308 4.34339 4.84667C4.34339 4.84869 4.34389 4.85097 4.34414 4.853C4.40835 5.52642 4.62177 6.15982 4.85425 6.78815C4.86503 6.81753 4.87581 6.8264 4.90892 6.81399C5.21939 6.69698 5.53012 6.58149 5.8416 6.46727C5.87896 6.4536 5.88423 6.43891 5.86918 6.40269C5.83031 6.30975 5.79671 6.21452 5.75934 6.12081C5.48021 5.42233 5.33526 4.69851 5.35081 3.9438C5.35256 3.85921 5.35632 3.77513 5.37112 3.6918C5.44109 3.29545 5.61313 2.94494 5.85113 2.6266C6.12198 2.26393 6.45904 1.98585 6.88613 1.82832C6.9566 1.80224 6.9571 1.80325 6.9571 1.87745C6.9571 2.66028 6.9576 3.4431 6.95585 4.22593C6.95585 4.27455 6.97039 4.29557 7.01528 4.31052C7.2904 4.40144 7.56501 4.49514 7.83937 4.58809C8.63161 4.8568 9.4236 5.12576 10.2158 5.39421C10.917 5.63202 11.6185 5.86908 12.3197 6.10663C13.0051 6.33887 13.688 6.57947 14.3767 6.80132C14.7629 6.92567 14.9873 7.26327 14.9828 7.6462C14.976 8.2411 14.9808 8.83626 14.9813 9.43117C14.9813 9.50309 14.978 9.57502 14.9637 9.64517C14.8612 10.1514 14.3937 10.4792 13.8891 10.3981C13.3121 10.3054 12.7355 10.211 12.1587 10.1175C11.8537 10.0681 11.5488 10.0187 11.2436 9.97112C11.1969 9.96377 11.1824 9.94984 11.1937 9.89843C11.234 9.71836 11.234 9.5345 11.2365 9.35088C11.237 9.31391 11.2197 9.31087 11.1909 9.31112C10.8649 9.31163 10.5391 9.31214 10.2131 9.31062C10.174 9.31062 10.1629 9.32353 10.1622 9.36101C10.1599 9.4727 10.1511 9.58388 10.1303 9.69405C9.94549 10.679 9.14974 11.4 8.16138 11.4648C7.50908 11.5076 6.95058 11.283 6.49415 10.8135C6.10843 10.4166 5.9078 9.93414 5.88849 9.37697C5.88724 9.34025 5.891 9.3096 5.83457 9.31011C5.51081 9.31289 5.18679 9.31214 4.86302 9.31062C4.8244 9.31062 4.81312 9.32125 4.81437 9.36051C4.82139 9.59503 4.84622 9.82701 4.90039 10.0555C5.05312 10.7005 5.36786 11.2511 5.84987 11.7019C6.29602 12.1193 6.81967 12.3827 7.4193 12.4867C7.47774 12.4969 7.49303 12.5164 7.4973 12.5721C7.53316 13.0333 7.61316 13.4869 7.73454 13.9331C7.9294 14.6503 8.23511 15.32 8.58923 15.9691C8.61155 16.0101 8.63161 16.0027 8.66346 15.9865C8.95212 15.8396 9.24052 15.6928 9.53068 15.5489C9.57307 15.5279 9.57156 15.5114 9.55225 15.4744C9.39601 15.1769 9.24554 14.8762 9.11111 14.5678C8.83048 13.9237 8.63412 13.2564 8.56615 12.5533C8.56264 12.5174 8.56339 12.4976 8.60879 12.4895C8.98672 12.4217 9.33883 12.2844 9.66385 12.0777C10.1012 11.7994 10.4466 11.4296 10.7189 10.9885C10.7407 10.9533 10.759 10.932 10.8067 10.9406C11.0367 10.9821 11.2674 11.0196 11.4979 11.0586C11.854 11.1186 12.2101 11.1774 12.566 11.2392C12.9976 11.3141 13.4317 11.3757 13.8618 11.461C13.9004 11.4686 13.9405 11.475 13.9799 11.474C14.5018 11.4626 14.9612 11.2865 15.3439 10.9264C15.7843 10.5116 15.9962 9.9934 15.9977 9.38685C15.9992 8.73268 15.9998 8.07876 16.0008 7.42459C16.0008 7.31468 15.9905 7.20527 15.9636 7.09865C15.8114 6.49412 15.4292 6.08865 14.8735 5.84628C14.5695 5.71383 14.2477 5.62721 13.9335 5.5211C12.9926 5.20351 12.0513 4.88719 11.1104 4.57036C10.106 4.23201 9.10209 3.89315 8.09718 3.55631C8.04326 3.53833 8.02445 3.51706 8.02445 3.45703C8.02696 2.54986 8.02546 1.64243 8.02771 0.735002C8.02771 0.677765 8.01292 0.665102 7.95875 0.666622C7.81254 0.670674 7.66633 0.668395 7.51987 0.667888C7.24902 0.667381 6.98268 0.700558 6.72161 0.77375C6.15784 0.932037 5.68285 1.23797 5.28384 1.66548C4.80033 2.18339 4.47831 2.78818 4.34289 3.48996C4.33612 3.52491 4.31982 3.53985 4.28997 3.55099C4.13749 3.60798 3.98828 3.67332 3.84106 3.74271C3.19428 4.04662 2.60719 4.43917 2.09709 4.94519C1.38936 5.64773 0.906848 6.49057 0.580573 7.43042C0.347842 8.10105 0.203389 8.7927 0.116365 9.49651C0.0416307 10.0937 0.00376181 10.6934 0.0020063 11.2954C0.000250788 11.8713 0.00150473 12.4475 0.00150473 13.0234Z'
                fill={isCurrentHorses ? '#1677ff' : 'black'}
                fillOpacity='0.88'
              />
              <path
                id='Vector_2'
                d='M0.0020063 13.0234C0.0020063 12.4475 0.000501575 11.8713 0.00250788 11.2954C0.00426339 10.6937 0.0421323 10.0937 0.115864 9.49675C0.202887 8.79294 0.347341 8.10129 0.580072 7.43066C0.906347 6.49082 1.38886 5.64797 2.09658 4.94543C2.60644 4.43916 3.19378 4.04686 3.84056 3.74295C3.98802 3.67381 4.13699 3.60847 4.28947 3.55124C4.31932 3.54009 4.33562 3.52515 4.34239 3.4902C4.47781 2.78867 4.79982 2.18364 5.28334 1.66572C5.68235 1.23822 6.15734 0.932028 6.72111 0.773995C6.98268 0.700803 7.24902 0.667626 7.51987 0.668132C7.66608 0.668385 7.81254 0.670918 7.95875 0.666866C8.01317 0.665346 8.02796 0.678009 8.02771 0.735246C8.02571 1.64242 8.02696 2.54985 8.02445 3.45728C8.02445 3.5173 8.04326 3.53857 8.09718 3.55655C9.10209 3.89314 10.1062 4.23225 11.1104 4.5706C12.0513 4.88769 12.9926 5.20401 13.9335 5.52134C14.2477 5.62746 14.5695 5.71382 14.8735 5.84653C15.4292 6.08889 15.8114 6.49436 15.9636 7.09889C15.9905 7.20526 16.0008 7.31467 16.0008 7.42484C16 8.07901 15.9995 8.73292 15.9977 9.38709C15.9962 9.99365 15.7846 10.5118 15.3439 10.9267C14.9612 11.287 14.5018 11.4631 13.9799 11.4742C13.9405 11.475 13.9007 11.4689 13.8618 11.4613C13.4317 11.3759 12.9978 11.3146 12.566 11.2394C12.2103 11.1776 11.854 11.1191 11.4979 11.0589C11.2674 11.0199 11.0367 10.9824 10.8067 10.9408C10.759 10.9322 10.7405 10.9535 10.7189 10.9887C10.4466 11.4299 10.1015 11.7996 9.66385 12.078C9.33883 12.2846 8.98672 12.4219 8.60879 12.4898C8.56339 12.4979 8.56264 12.5176 8.56615 12.5536C8.63412 13.2564 8.83023 13.924 9.11111 14.568C9.24554 14.8765 9.39601 15.1771 9.55225 15.4747C9.57181 15.5119 9.57332 15.5281 9.53068 15.5491C9.24077 15.693 8.95212 15.8401 8.66346 15.9868C8.63161 16.003 8.61155 16.0101 8.58923 15.9693C8.23511 15.3202 7.9294 14.6506 7.73454 13.9333C7.61316 13.4871 7.53341 13.0335 7.4973 12.5723C7.49303 12.5169 7.47774 12.4971 7.4193 12.487C6.81967 12.3829 6.29602 12.1195 5.84987 11.7021C5.36786 11.2513 5.05312 10.7007 4.90039 10.0557C4.84622 9.82726 4.82139 9.59527 4.81437 9.36075C4.81312 9.3215 4.8244 9.31086 4.86302 9.31086C5.18679 9.31238 5.51081 9.31314 5.83457 9.31035C5.89075 9.30985 5.88724 9.34049 5.88849 9.37721C5.90755 9.93464 6.10818 10.4168 6.49415 10.8137C6.95033 11.2832 7.50883 11.5079 8.16138 11.4651C9.14949 11.4003 9.94549 10.6792 10.1303 9.69429C10.1511 9.58413 10.1599 9.47295 10.1622 9.36126C10.1629 9.32378 10.174 9.31086 10.2131 9.31086C10.5391 9.31238 10.8649 9.31187 11.1909 9.31137C11.2197 9.31137 11.237 9.31415 11.2365 9.35113C11.234 9.53474 11.234 9.71861 11.1937 9.89867C11.1821 9.95009 11.1967 9.96402 11.2436 9.97136C11.5488 10.019 11.8537 10.0684 12.1587 10.1177C12.7355 10.2112 13.3121 10.3059 13.8891 10.3984C14.3937 10.4794 14.8612 10.1517 14.9637 9.64542C14.978 9.57526 14.9813 9.50334 14.9813 9.43141C14.9808 8.8365 14.976 8.24135 14.9828 7.64644C14.9871 7.26351 14.7629 6.92592 14.3767 6.80157C13.688 6.57971 13.0051 6.33937 12.3197 6.10688C11.6185 5.86907 10.917 5.63201 10.2158 5.39446C9.4236 5.126 8.63161 4.85704 7.83937 4.58833C7.56476 4.49513 7.2904 4.40168 7.01528 4.31076C6.97039 4.29582 6.95585 4.2748 6.95585 4.22617C6.9576 3.44335 6.9571 2.66052 6.9571 1.8777C6.9571 1.80349 6.95685 1.80248 6.88613 1.82857C6.45904 1.98609 6.12198 2.26417 5.85113 2.62684C5.61338 2.94519 5.44109 3.2957 5.37112 3.69205C5.35632 3.77537 5.35256 3.85945 5.35081 3.94404C5.33501 4.69875 5.48021 5.42282 5.75934 6.12106C5.79671 6.21476 5.83031 6.30999 5.86918 6.40294C5.88423 6.43915 5.87896 6.45359 5.8416 6.46752C5.53012 6.58174 5.21939 6.69722 4.90892 6.81423C4.87606 6.82664 4.86528 6.81778 4.85425 6.7884C4.62151 6.16006 4.40835 5.52691 4.34414 4.85324C4.34414 4.85122 4.34364 4.84894 4.34339 4.84691C4.3316 4.72332 4.3306 4.72129 4.22101 4.77169C3.30262 5.19261 2.57609 5.83006 2.06699 6.71445C1.80241 7.17411 1.61131 7.66442 1.46184 8.17271C1.4483 8.21855 1.45432 8.23501 1.50698 8.23476C1.87464 8.23274 2.24254 8.23476 2.6102 8.23223C2.66562 8.23172 2.67942 8.24641 2.67891 8.30238C2.6759 8.61668 2.6759 8.93122 2.67891 9.24552C2.67942 9.30352 2.66186 9.31466 2.60844 9.31441C2.16956 9.31238 1.73094 9.3139 1.29206 9.31213C1.24516 9.31213 1.22886 9.32555 1.21908 9.3719C1.1155 9.8693 1.07814 10.373 1.06936 10.8801C1.06836 10.937 1.0982 10.9332 1.13557 10.9332C1.6246 10.933 2.11339 10.934 2.60242 10.9317C2.66236 10.9315 2.67992 10.9469 2.67916 11.0085C2.67615 11.3228 2.67666 11.6373 2.67891 11.9516C2.67916 12.0012 2.66512 12.0159 2.61572 12.0157C2.12467 12.0139 1.63363 12.0154 1.14259 12.0134C1.08792 12.0134 1.0681 12.024 1.06861 12.0853C1.07136 12.5769 1.07111 13.0685 1.06911 13.5603C1.06911 13.6163 1.08039 13.6345 1.13958 13.6343C1.62861 13.6317 2.1174 13.6337 2.60644 13.6312C2.66437 13.631 2.67942 13.6469 2.67891 13.7047C2.67615 14.0149 2.6759 14.3249 2.67891 14.6351C2.67942 14.6972 2.66688 14.7174 2.60092 14.7169C2.11615 14.7139 1.63137 14.7169 1.1466 14.7136C1.08089 14.7131 1.06785 14.7326 1.06836 14.7954C1.07162 15.1097 1.06886 15.4243 1.07111 15.7386C1.07136 15.7864 1.05832 15.7981 1.01168 15.7978C0.696186 15.7958 0.380696 15.7953 0.0652048 15.7978C0.0135425 15.7984 0 15.7862 0 15.733C0.00175551 14.8299 0.00125394 13.9268 0.00125394 13.0236H0.00150473L0.0020063 13.0234Z'
                fill={isCurrentHorses ? '#1677ff' : 'black'}
                fillOpacity='0.88'
              />
            </g>
            <defs>
              <clipPath id='clip0_6549_12417'>
                <rect
                  width='16'
                  height='15.3333'
                  fill='white'
                  transform='translate(0 0.666626)'
                />
              </clipPath>
            </defs>
          </svg>
        )
      },
      {
        label: 'Продвижение',
        key: superAdminRoutes.MAIN,
        icon: <RiseOutlined />
      },
      {
        label: 'Промокоды',
        key: superAdminRoutes.PROMOCODES,
        icon: <SwitcherOutlined />
      },
      {
        label: 'Скидки',
        key: superAdminRoutes.DISCOUNTS,
        icon: <PercentageOutlined />
      },
      {
        label: 'ПолоКлуб',
        key: superAdminRoutes.POLO_CLUB,
        icon: <HorseIcon />
      },
      {
        label: <MenuCountBadge count={actionsCount}>Действия</MenuCountBadge>,
        key: superAdminRoutes.ACTIONS,
        icon: <GlobalOutlined />
      }
    ],
    [isCurrentHorses, actionsCount, supportUnreadCount, moderationHorsesCount]
  )

  React.useEffect(() => {
    const handleWindowResize = () => {
      setWindowWidth(getWindowWidth())
    }

    window.addEventListener('resize', handleWindowResize)

    return () => {
      window.removeEventListener('resize', handleWindowResize)
    }
  }, [])

  React.useEffect(() => {
    setCurrent(`/${location.pathname.split('/')[1]}`)
    if (usersMenuItems.some((i) => i.key === `/${location.pathname.split('/')[1]}`)) {
      setCurrentUserMenu(`/${location.pathname.split('/')[1]}`)
    }
    if (
      collectionsMenuItems.some(
        (i) => i.key === `/${location.pathname.split('/')[1]}`
      )
    ) {
      setCurrentCollectionsMenu(`/${location.pathname.split('/')[1]}`)
    }
    if (
      `/${location.pathname.split('/')[1]}` === superAdminRoutes.CATEGORIES ||
      `/${location.pathname.split('/')[1]}` === superAdminRoutes.GOODS ||
      `/${location.pathname.split('/')[1]}` === superAdminRoutes.HORSES
    ) {
      setCurrentGoodsMenu(
        `/${location.pathname.split('/')[1]}` === superAdminRoutes.CATEGORIES
          ? superAdminRoutes.CATEGORIES
          : `/${location.pathname.split('/')[1]}` === superAdminRoutes.HORSES
            ? superAdminRoutes.HORSES
            : superAdminRoutes.GOODS
      )
    }
  }, [location.pathname])

  return (
    <>
      {/* без `windowWidth &&`: при 0 React рендерил бы сам ноль вместо меню */}
      {windowWidth >= 1050 && !isTouchLandscape ? (
        <Menu
          onClick={onClick}
          selectedKeys={[current]}
          mode='horizontal'
          items={items}
          disabledOverflow
        />
      ) : (
        <MobileMenu
          isOpen={isOpenMobileMenu}
          setIsOpen={setIsOpenMobileMenu}
          Menu={
            <Menu
              onClick={onClick}
              selectedKeys={[current]}
              mode='vertical'
              items={mobileItems}
              style={{ borderInlineEnd: 'none' }}
            />
          }
        />
      )}
    </>
  )
}
